'use client'
import { PropsWithChildren, useEffect } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useTrackingStore } from '@/hooks/use-tracking/store'
import { qParamsSelector } from '@/hooks/use-tracking/selectors'
import { salesChatCheckoutLanded } from '@/actions/sales-chat'
import {
  isSalesChatRef,
  rememberSalesChatRef,
  withoutSalesChatRef,
} from '@/utils/sales-chat-ref'

export function QueryParam({ children }: PropsWithChildren) {
  const params = useSearchParams()
  const { setQueryParams } = useTrackingStore(qParamsSelector)
  const path = usePathname()
  const router = useRouter()

  useEffect(() => {
    if (params.size > 0) {
      const list = getListFromParams(params)
      // El `ref` del asistente de ventas (`?ref=sc_…`) NO va al store de tracking
      // (localStorage: sobreviviría al cierre del navegador y una visita de meses
      // después reenviaría un `ref` viejo). Va a sessionStorage: sobrevive a los
      // pasos del funnel aunque la URL se limpie aquí mismo, y `TrackingComponent`
      // lo manda en cada paso como `tracking.sales_chat_ref`; muere con la pestaña.
      rememberSalesChatRef(list.ref)
      setQueryParams(withoutSalesChatRef(list))
      // Aterrizaje desde el bot: se avisa UNA vez (los params se borran de la URL
      // justo después, así que este efecto no vuelve a entrar con `ref`).
      if (isSalesChatRef(list.ref)) {
        const funnel = getFunnel(path)
        salesChatCheckoutLanded({
          ref: list.ref,
          academy: funnel?.academy,
          plan: funnel?.product,
          utmSource: list.utm_source,
          utmMedium: list.utm_medium,
          utmCampaign: list.utm_campaign,
        }).catch(() => null)
      }
      router.replace(path)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])
  return <>{children}</>
}

function getListFromParams(params: URLSearchParams) {
  const list: Record<string, string> = {}
  params.forEach((value, key) => {
    list[key] = value
  })
  return list
}

/** `/payment/<academy>/<product>/<step>` → sus segmentos (o null si no encaja). */
function getFunnel(path: string) {
  const paths = path.split('/').filter((segment) => segment !== '')
  if (paths.length > 2) {
    return { route: paths[0], academy: paths[1], product: paths[2] }
  }
  return null
}
